import { apiFetch } from '@/lib/api'

export interface ChatAgent {
  id: number
  key: string
  name: string
}

export interface ChatRoomSummary {
  id: number
  kind: 'team' | 'group' | 'direct' | 'feed'
  title: string
  agent: ChatAgent | null
  working: boolean
  updated_at: string
  preview: string
  waiting: number
}

export interface ChatCard {
  id: number
  kind: 'approval' | 'question' | 'alert'
  kind_label: string
  status: 'open' | 'approved' | 'rejected' | 'answered' | 'failed'
  status_label: string
  severity: 'low' | 'normal' | 'high' | 'urgent'
  title: string
  why: string
  action: string
  money: boolean
  details: Array<{ label: string; value: string }>
  draft: string
  error: string
  answer: string
  decided_by: string
  toolkit: string
  /** What saying yes costs; amount is null when the request doesn't say. */
  price: { amount: number | null; label: string } | null
  /** Quick choices to tap instead of typing an answer. */
  questions: ChatQuestion[]
  /** "Don't ask me this again" applies (questions, and small reversible actions). */
  can_remember: boolean
  /** Pictures the action made (generated ad images). */
  images: string[]
  /** Approved, and still being made in the background. */
  generating: boolean
  /** Failed in a way that can simply be run again (image generation). */
  retryable: boolean
}

export interface ChatQuestion {
  question: string
  options: string[]
  multi: boolean
}

export interface ChatMessage {
  id: number
  role: 'admin' | 'agent' | 'step' | 'approval' | 'system'
  text: string
  created_at: string
  agent: ChatAgent | null
  data: {
    status?: 'running' | 'done' | 'error'
    tool?: string
    error?: string
    doing?: string
    done?: string
    summary?: string
    icon?: string
    finished_at?: string
    parent?: number | null
    routine?: string
    args?: Record<string, unknown>
    background?: boolean
    trigger?: string
    to?: string
    /** A workflow built in this chat: its card shows here. */
    workflow_card?: number
    refine?: boolean
    /** Numbers an agent drew with show_data: stat tiles, a chart or a table. */
    widget?: import('@/pages/ai-team/DataWidget').Widget
    /** A line saying the team learned something; Undo deletes it. */
    memory?: number
    memory_kind?: 'fact' | 'rule' | 'allow'
    undone?: boolean
    /** Chief of Staff's questions before building this workflow. */
    workflow_questions?: number
    /** What a workflow's smart check found, with Right / Wrong buttons. */
    classify?: {
      workflow: number
      node: string
      label: string
      items: import('@/lib/workflows').ClassifyMatch[]
      stats: import('@/lib/workflows').ClassifyStats
    }
  }
  card: ChatCard | null
}

export interface ChatMember extends ChatAgent {
  active: boolean
  working: boolean
  lead: boolean
}

export interface ChatActivity {
  id: number
  agent: string
  label: string
  summary: string
  icon: string
  toolkit: string
  status: 'running' | 'done' | 'error' | 'waiting'
  kind: 'step' | 'approval'
  at: string
}

export interface ChatRoomDetail extends ChatRoomSummary {
  messages: ChatMessage[]
  members: ChatMember[]
  stats: {
    messages: number
    tool_calls: number
    waiting: number
    decided: number
    runs: number
    cost: string
  }
  activity: ChatActivity[]
  map: {
    edges: Array<{ from: string; to: string; count: number }>
    agents: Array<{
      name: string
      runs: number
      tokens_in: number
      tokens_out: number
      cost: number
    }>
    total: { tokens: number; cost: number }
  }
}

export const fetchRooms = () =>
  apiFetch<{ team: ChatRoomSummary; automations: ChatRoomSummary; rooms: ChatRoomSummary[] }>(
    '/api/admin/ai-team/rooms/'
  )

export const fetchRoom = (id: number) =>
  apiFetch<ChatRoomDetail>(`/api/admin/ai-team/rooms/${id}/`)

/** No agent = a new chat led by Chief of Staff; with an agent = a one-to-one. */
export const createRoom = (agent?: string) =>
  apiFetch<ChatRoomSummary>('/api/admin/ai-team/rooms/', {
    method: 'POST',
    body: agent ? { agent } : {}
  })

export const addMember = (room: number, agent: string) =>
  apiFetch(`/api/admin/ai-team/rooms/${room}/members/`, {
    method: 'POST',
    body: { agent }
  })

export const kickMember = (room: number, agent: string) =>
  apiFetch(`/api/admin/ai-team/rooms/${room}/members/${agent}/`, {
    method: 'DELETE'
  })

export const archiveRoom = (id: number) =>
  apiFetch<ChatRoomSummary>(`/api/admin/ai-team/rooms/${id}/`, {
    method: 'PATCH',
    body: { archived: true }
  })

/** Stop everything working in a chat: agents, workflow runs posting there, workflow builds. */
export const stopRoom = (id: number) =>
  apiFetch<{ agents: number; workflow_runs: number; builds: number }>(
    `/api/admin/ai-team/rooms/${id}/stop/`,
    { method: 'POST' }
  )

export const sendMessage = (id: number, text: string) =>
  apiFetch<ChatMessage>(`/api/admin/ai-team/rooms/${id}/messages/`, {
    method: 'POST',
    body: { text }
  })

export const decideCard = (
  id: number,
  decision: 'approve' | 'reject' | 'answer' | 'retry',
  note = '',
  /** Turn this decision into a standing rule so the team doesn't ask again. */
  remember = false
) =>
  apiFetch<ChatCard>(`/api/admin/ai-team/cards/${id}/`, {
    method: 'POST',
    body: { decision, note, ...(remember ? { remember: true } : {}) }
  })

export interface TeamAgent {
  id: number
  key: string
  name: string
  layer: string
  layer_label: string
  duty: string
  email: string
  enabled: boolean
  runs_today: number
  failed_today: number
  waiting: number
  open_threads: number
  /** Runs going right now. */
  working: number
  /** Dollars, as a 2-decimal string. */
  spent_today: string
  daily_budget_usd: string
  last_run_at: string | null
  last_summary: string
  quick_tasks: QuickTask[]
}

/** A one-click job: "{choice}" in `task` is replaced by the option the admin picks. */
export interface QuickTask {
  id: number
  label: string
  task: string
  choice: { label: string; options: string[] } | null
  enabled: boolean
  order: number
  /** Copied from code: hiding keeps it, so it isn't copied back. */
  builtin: boolean
  /** Agent key. */
  agent: string
}

/** What the Settings → Jobs form sends. */
export interface JobInput {
  agent?: string
  label?: string
  task?: string
  choice_label?: string
  options?: string[]
  enabled?: boolean
  order?: number
}

export const fetchJobs = () => apiFetch<{ jobs: QuickTask[] }>('/api/admin/ai-team/jobs/')

export const createJob = (body: JobInput) =>
  apiFetch<QuickTask>('/api/admin/ai-team/jobs/', { method: 'POST', body })

export const updateJob = (id: number, body: JobInput) =>
  apiFetch<QuickTask>(`/api/admin/ai-team/jobs/${id}/`, { method: 'PATCH', body })

/** Removes a job the admin made; hides a built-in one. */
export const deleteJob = (id: number) =>
  apiFetch(`/api/admin/ai-team/jobs/${id}/`, { method: 'DELETE' })

export interface TeamResponse {
  team_on: boolean
  owner_name: string
  owner_email: string
  settings_id: number
  layers: Array<{ id: string; label: string }>
  agents: TeamAgent[]
  spent_today: string
  /** Sum of the switched-on agents' daily budgets. */
  budget_today: string
}

// Defaults keep the page working against an API from before these fields existed.
export const fetchTeam = async (): Promise<TeamResponse> => {
  const data = await apiFetch<TeamResponse>('/api/admin/ai-team/')
  return {
    ...data,
    spent_today: data.spent_today ?? '0.00',
    budget_today: data.budget_today ?? '0.00',
    agents: data.agents.map((a) => ({
      ...a,
      working: a.working ?? 0,
      spent_today: a.spent_today ?? '0.00',
      daily_budget_usd: a.daily_budget_usd ?? '0.00',
      quick_tasks: a.quick_tasks ?? []
    }))
  }
}

export interface AccountConnection {
  toolkit: string
  name: string
  category: string
  status: 'not_connected' | 'pending' | 'active' | 'error'
  status_label: string
  money: boolean
  error: string
  connected_at: string | null
  has_auth_config: boolean
  env_var: string
  agents: string[]
}

export const fetchConnections = () =>
  apiFetch<{
    categories: string[]
    connections: AccountConnection[]
    agents: ChatAgent[]
  }>('/api/admin/ai-team/connections/')

export const updateConnection = (
  toolkit: string,
  body: { agents?: string[] }
) =>
  apiFetch<AccountConnection>(`/api/admin/ai-team/connections/${toolkit}/`, {
    method: 'PATCH',
    body
  })

export const connectionAction = (
  toolkit: string,
  action: 'connect' | 'refresh' | 'disconnect'
) =>
  apiFetch<AccountConnection & { redirect_url?: string }>(
    `/api/admin/ai-team/connections/${toolkit}/${action}/`,
    { method: 'POST' }
  )

export const renameRoom = (id: number, title: string) =>
  apiFetch<ChatRoomSummary>(`/api/admin/ai-team/rooms/${id}/`, {
    method: 'PATCH',
    body: { title }
  })

export const deleteRoom = (id: number) =>
  apiFetch(`/api/admin/ai-team/rooms/${id}/`, { method: 'DELETE' })

export interface ApprovalItem extends ChatCard {
  agent: ChatAgent
  created_at: string
  room: { id: number; title: string; kind: ChatRoomSummary['kind'] } | null
  source: string
}

export const fetchApprovals = () =>
  apiFetch<{ count: number; items: ApprovalItem[] }>(
    '/api/admin/ai-team/approvals/'
  )

// --- Models & pricing --------------------------------------------------------

export interface TextModel {
  id: string
  name: string
  maker: string
  origin: string
  note: string
  /** $ per 1M tokens. */
  price_in: number
  price_out: number
  /** What a typical chat reply costs on this model. */
  per_turn: number
  /** Price came live from OpenRouter (false = fallback figure). */
  live: boolean
  tested: boolean
}

export interface ImageModel {
  id: string
  name: string
  maker: string
  origin: string
  note: string
  per_image: number
  live: boolean
}

export interface ModelSettings {
  text_model: string
  image_model: string
  default_text_model: string
  default_image_model: string
  text: TextModel[]
  image: ImageModel[]
  agents: Array<{ key: string; name: string; model: string }>
  spend_7d: number
  runs_7d: number
  turn: { tokens_in: number; tokens_out: number }
}

export const fetchModels = () =>
  apiFetch<ModelSettings>('/api/admin/ai-team/models/')

export const updateModels = (body: {
  text_model?: string
  image_model?: string
  agents?: Record<string, string>
}) =>
  apiFetch<ModelSettings>('/api/admin/ai-team/models/', {
    method: 'PATCH',
    body
  })

// --- A team's page: its work log, emails and reports -------------------------

export interface TeamWork {
  runs: Array<{
    id: number
    agent: ChatAgent
    trigger: string
    trigger_label: string
    task: string
    status: 'running' | 'done' | 'waiting' | 'failed' | 'skipped' | 'stopped'
    status_label: string
    summary: string
    error: string
    cost: string
    started_at: string
    finished_at: string | null
    room: number | null
    /** Open decisions this run is waiting on. */
    cards: number[]
  }>
  threads: Array<{
    id: number
    agent: ChatAgent
    subject: string
    contact: string
    status: string
    status_label: string
    updated_at: string
  }>
  reports: Array<{ agent: ChatAgent; date: string; period: string; summary: string }>
  page: number
  pages: number
  total: number
  /** Runs waiting on the owner, across all pages. */
  waiting_total: number
}

export const fetchTeamWork = (layers: string[], page = 1, show: 'all' | 'waiting' | 'failed' = 'all') =>
  apiFetch<TeamWork>(
    `/api/admin/ai-team/work/?layers=${encodeURIComponent(layers.join(','))}&page=${page}&show=${show}`
  )
